import { useCallback, useEffect, useRef, useState } from 'react';

export type PreviewStatus = 'requesting' | 'ready' | 'denied' | 'unavailable';

export interface MediaPreview {
  status: PreviewStatus;
  /** Set when a device could not be opened, phrased for a candidate, not a developer. */
  problem: string | null;
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
  cameraId: string;
  microphoneId: string;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  selectCamera: (deviceId: string) => void;
  selectMicrophone: (deviceId: string) => void;
  setCameraEnabled: (enabled: boolean) => void;
  setMicrophoneEnabled: (enabled: boolean) => void;
  /**
   * Hands the hardware back immediately. Stable across renders, so it is safe
   * to use as an effect cleanup.
   */
  release: () => void;
}

function describeDeviceError(err: unknown): { status: PreviewStatus; problem: string } {
  const name = err instanceof DOMException ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return {
        status: 'denied',
        problem:
          'Your browser blocked camera and microphone access. Allow it from the padlock in the address bar, then reload.',
      };
    case 'NotFoundError':
    case 'OverconstrainedError':
      return {
        status: 'unavailable',
        problem: 'We could not find a camera or microphone on this device.',
      };
    case 'NotReadableError':
      return {
        status: 'unavailable',
        problem:
          'Another app is already using your camera. Close Zoom, Teams or FaceTime and reload this page.',
      };
    default:
      return { status: 'unavailable', problem: 'We could not start your camera and microphone.' };
  }
}

/**
 * Device preview for the pre-join screen, owned by us rather than by LiveKit's
 * PreJoin prefab.
 *
 * Camera and microphone are acquired as two independent tracks so they can be
 * switched and toggled without tearing the other one down. Turning the camera
 * off stops its track outright instead of just muting it, because people judge
 * "is it really off?" by whether the hardware light goes out.
 */
export function useMediaPreview(enabled: boolean): MediaPreview {
  const [status, setStatus] = useState<PreviewStatus>('requesting');
  const [problem, setProblem] = useState<string | null>(null);

  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null);

  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);

  const [cameraId, setCameraId] = useState('');
  const [microphoneId, setMicrophoneId] = useState('');
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [microphoneEnabled, setMicrophoneEnabledState] = useState(true);

  // Read at acquisition time so muting does not have to be a dependency of the
  // effect that opens the device — otherwise every mute re-opens the mic.
  const micEnabledRef = useRef(microphoneEnabled);
  micEnabledRef.current = microphoneEnabled;

  // `release` is handed to an effect cleanup, so it must never change identity:
  // a `release` that changed whenever a track changed would be invoked the
  // instant the camera opened, and stop the preview it had just started.
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);
  videoTrackRef.current = videoTrack;
  audioTrackRef.current = audioTrack;

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    // Labels are empty strings until permission is granted; filtering them out
    // keeps a menu of anonymous "Device 1 / Device 2" entries off the screen.
    setCameras(devices.filter((d) => d.kind === 'videoinput' && d.deviceId));
    setMicrophones(devices.filter((d) => d.kind === 'audioinput' && d.deviceId));
  }, []);

  // One up-front prompt for both permissions. Asking twice (once per device)
  // produces two browser dialogs, which reads as a broken page.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('unavailable');
        setProblem('This browser cannot access a camera. Try Chrome, Safari or Edge.');
        return;
      }

      try {
        const probe = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        // The probe exists only to unlock device labels; the real tracks are
        // acquired below against the chosen deviceIds.
        probe.getTracks().forEach((track) => track.stop());
        if (cancelled) return;
        await refreshDevices();
        if (cancelled) return;
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        const { status: next, problem: message } = describeDeviceError(err);
        setStatus(next);
        setProblem(message);
      }
    })();

    navigator.mediaDevices?.addEventListener?.('devicechange', refreshDevices);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', refreshDevices);
    };
  }, [enabled, refreshDevices]);

  // Camera track lifecycle.
  useEffect(() => {
    if (!enabled || status !== 'ready' || !cameraEnabled) {
      setVideoTrack(null);
      return;
    }

    let cancelled = false;
    let acquired: MediaStreamTrack | null = null;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: cameraId ? { deviceId: { exact: cameraId } } : true,
        });
        const [track] = stream.getVideoTracks();
        if (!track) return;
        if (cancelled) {
          track.stop();
          return;
        }
        acquired = track;
        setVideoTrack(track);
        setProblem(null);
        // The label is only readable once a track is open on some browsers.
        void refreshDevices();
      } catch (err) {
        if (cancelled) return;
        setProblem(describeDeviceError(err).problem);
        setVideoTrack(null);
      }
    })();

    return () => {
      cancelled = true;
      acquired?.stop();
    };
  }, [enabled, status, cameraEnabled, cameraId, refreshDevices]);

  // Microphone track lifecycle. Unlike the camera the track stays open while
  // muted, so the level meter can keep proving the right device is selected.
  useEffect(() => {
    if (!enabled || status !== 'ready') {
      setAudioTrack(null);
      return;
    }

    let cancelled = false;
    let acquired: MediaStreamTrack | null = null;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: microphoneId
            ? { deviceId: { exact: microphoneId }, echoCancellation: true, noiseSuppression: true }
            : { echoCancellation: true, noiseSuppression: true },
        });
        const [track] = stream.getAudioTracks();
        if (!track) return;
        if (cancelled) {
          track.stop();
          return;
        }
        track.enabled = micEnabledRef.current;
        acquired = track;
        setAudioTrack(track);
        void refreshDevices();
      } catch (err) {
        if (cancelled) return;
        setProblem(describeDeviceError(err).problem);
        setAudioTrack(null);
      }
    })();

    return () => {
      cancelled = true;
      acquired?.stop();
    };
  }, [enabled, status, microphoneId, refreshDevices]);

  useEffect(() => {
    if (audioTrack) audioTrack.enabled = microphoneEnabled;
  }, [audioTrack, microphoneEnabled]);

  const release = useCallback(() => {
    videoTrackRef.current?.stop();
    audioTrackRef.current?.stop();
    setVideoTrack(null);
    setAudioTrack(null);
  }, []);

  return {
    status,
    problem,
    videoTrack,
    audioTrack,
    cameras,
    microphones,
    cameraId,
    microphoneId,
    cameraEnabled,
    microphoneEnabled,
    selectCamera: setCameraId,
    selectMicrophone: setMicrophoneId,
    setCameraEnabled,
    setMicrophoneEnabled: setMicrophoneEnabledState,
    release,
  };
}
